import React from 'react';
import classNames from 'classnames';
import { FieldTextInput, FieldCheckbox } from '../../components';
import { FormattedMessage } from '../../util/reactIntl';
import {
  REQUIRED_UPFRONT,
  feeTypeMapper,
  FLAT_RATE,
  PERCENTAGE_OF_RENTAL,
} from '../../util/constants';
import { required } from '../../util/validators';
import { formatMoney } from '../../util/currency';
import feesCss from '../OrderPanel/FeesInputFields/FeesInputFields.module.css';

/**
 * Shared cart-level add-ons: fees configured ONLY_CHARGE_ONCE_FOR_ENTIRE_CART on one or
 * more cart items (see getCartAddonsConfig in cartModalHelpers.js, and
 * CartDrawerItem.js/CartModalItemFields.js's own per-item filtering, which deliberately
 * excludes these from their own per-item rendering). Rendered exactly once here rather
 * than once per item, using whichever item's config had the highest rate for a given
 * feeType+feeCalculationType group - the same "highest wins" pattern
 * DeliveryReturnSelectField already applies to delivery/pickup, generalized to every
 * other addon. Consumed by both the Listing Page cart drawer (CartDrawer) and the Store
 * Profile cart panel (CartModal) - see the cart-consistency plan.
 *
 * Field names deliberately reuse the same `global.optionalFees` array
 * DeliveryReturnSelectField already writes DELIVERY_FEE/PICKUP_FEE into (see
 * buildItemsForAPI in cartModalHelpers.js, which reads this same array back for every
 * oncePerCart feeType, not just those two) - not a separate namespace, so a general
 * addon and a delivery/pickup selection can coexist in one flat selected-list. Usage-
 * multiple values get their own `global.addonUsage.{feeType}` field, parallel to
 * `global.deliveryFee`/`global.pickupFee` but keyed generically so any feeType can use
 * it without colliding with those two reserved names.
 */
const CartAddonsField = props => {
  const { intl, disabled, values, addons, onRemoveOptionalFee, onRemoveUsageFee, className } = props;
  const { usageMultipleCharges = [], optionalCharges = [], requiredCharges = [] } =
    addons || {};

  // RAS-2504: same "only show what was already selected, required fees stay
  // unconditional" rule as CartDrawerItem's per-item section - see that component's own
  // doc comment for why required fees (checkbox and usage-multiple alike) are excluded
  // from this filter rather than gated on the group's shared selection state.
  const selectedOptionalCharges = optionalCharges.filter(fee =>
    values?.global?.optionalFees?.includes(fee.feeType)
  );
  const requiredUsageMultipleCharges = usageMultipleCharges.filter(
    charge => charge.whenToApply === REQUIRED_UPFRONT
  );
  const selectedOptionalUsageMultipleCharges = usageMultipleCharges.filter(charge => {
    if (charge.whenToApply === REQUIRED_UPFRONT) return false;
    const usageValue = values?.global?.addonUsage?.[charge.feeType];
    return usageValue != null && usageValue !== '';
  });

  const hasAddons =
    requiredUsageMultipleCharges.length > 0 ||
    selectedOptionalUsageMultipleCharges.length > 0 ||
    selectedOptionalCharges.length > 0 ||
    requiredCharges.length > 0;

  if (!hasAddons) return null;

  return (
    <div className={classNames(feesCss.otherFeesContainer, className)}>
      {requiredUsageMultipleCharges.map(charge => {
        const { feeType, rate, usageLabel, whenToApply } = charge;
        const isRequired = whenToApply === REQUIRED_UPFRONT;
        return (
          <FieldTextInput
            key={feeType}
            type="number"
            className={feesCss.usageField}
            min={1}
            id={`global_${feeType}`}
            name={`global.addonUsage.${feeType}`}
            label={intl.formatMessage(
              { id: 'UsageMultipleChargeFieldMaybe.UsageMultipleCharges.label' },
              {
                feeType: feeTypeMapper[feeType] || feeType,
                text: usageLabel,
                rate: formatMoney(intl, rate),
                isRequired,
              }
            )}
            {...(isRequired
              ? {
                  validate: required(
                    intl.formatMessage({
                      id: 'UsageMultipleChargeFieldMaybe.UsageMultipleCharges.required',
                    })
                  ),
                }
              : {})}
            disabled={disabled}
          />
        );
      })}

      {selectedOptionalUsageMultipleCharges.length > 0 && (
        <div className={feesCss.otherFeesContainer}>
          {selectedOptionalUsageMultipleCharges.map(charge => (
            <div key={charge.feeType} className={classNames(feesCss.otherFee, feesCss.active)}>
              <div className={feesCss.otherFeeRow}>
                <div className={feesCss.feeLabel}>
                  <div>
                    <div className={feesCss.deliveryText}>
                      {feeTypeMapper[charge.feeType] || charge.feeType}
                      {charge.usageLabel
                        ? `: ${values?.global?.addonUsage?.[charge.feeType]} ${charge.usageLabel}`
                        : ''}
                    </div>
                    <div className={feesCss.feeDescription}>{charge.feeDescription}</div>
                  </div>
                  <span className={feesCss.priceText}>{formatMoney(intl, charge.rate)}</span>
                </div>
                <button
                  type="button"
                  className={feesCss.removeAddonButton}
                  onClick={() => onRemoveUsageFee(charge.feeType)}
                  disabled={disabled}
                  aria-label={intl.formatMessage({
                    id: 'CartDrawer.removeAddon',
                    defaultMessage: 'Remove add-on',
                  })}
                >
                  <i className="ri-close-line" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedOptionalCharges.length > 0 && (
        <div className={feesCss.otherFeesContainer}>
          {selectedOptionalCharges.map(fee => (
            <div key={fee.feeType} className={classNames(feesCss.otherFee, feesCss.active)}>
              <div className={feesCss.otherFeeRow}>
                <div className={feesCss.feeLabel}>
                  <div>
                    <div className={feesCss.deliveryText}>
                      <FormattedMessage
                        id="OtherOptionalChargesField.OtherOptionalFees.label"
                        values={{ feeType: fee.feeType }}
                      />
                    </div>
                    <div className={feesCss.feeDescription}>{fee.feeDescription}</div>
                  </div>
                  <span className={feesCss.priceText}>
                    {fee.feeCalculationType === FLAT_RATE
                      ? formatMoney(intl, fee.rate)
                      : fee.feeCalculationType === PERCENTAGE_OF_RENTAL
                      ? intl.formatMessage(
                          { id: 'OtherOptionalChargesField.OtherOptionalFees.percentage' },
                          { percentage: fee.percentage }
                        )
                      : null}
                  </span>
                </div>
                <button
                  type="button"
                  className={feesCss.removeAddonButton}
                  onClick={() => onRemoveOptionalFee(fee.feeType)}
                  disabled={disabled}
                  aria-label={intl.formatMessage({
                    id: 'CartDrawer.removeAddon',
                    defaultMessage: 'Remove add-on',
                  })}
                >
                  <i className="ri-close-line" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {requiredCharges.length > 0 && (
        <div className={feesCss.otherFeesContainer}>
          {requiredCharges.map(fee => (
            <div
              key={fee.feeType}
              className={classNames(feesCss.otherFee, feesCss.disabled, feesCss.active)}
            >
              <FieldCheckbox
                id={`global_required_${fee.feeType}`}
                name="global.optionalFees"
                value={fee.feeType}
                disabled={true}
                checked={true}
                label={
                  <div className={feesCss.feeLabel}>
                    <div>
                      <div className={feesCss.deliveryText}>
                        <FormattedMessage
                          id="RequiredChargesField.label"
                          values={{ feeType: fee.feeType }}
                          defaultMessage="{feeType}"
                        />
                      </div>
                      <div className={feesCss.feeDescription}>{fee.feeDescription}</div>
                    </div>
                    <span className={feesCss.priceText}>
                      {fee.feeCalculationType === FLAT_RATE
                        ? formatMoney(intl, fee.rate)
                        : fee.feeCalculationType === PERCENTAGE_OF_RENTAL
                        ? intl.formatMessage(
                            { id: 'RequiredChargesField.percentage', defaultMessage: '{percentage}%' },
                            { percentage: fee.percentage }
                          )
                        : null}
                    </span>
                  </div>
                }
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CartAddonsField;
